import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Name from "_shared/names/name";
import { ownIndividualProfileRoute } from "marketplace/_views/profiles/own-individual-profile-route";
import { useOwnIndividual } from "marketplace/individuals/own-individual";
import { Link as RouterLink } from "react-router";
import { useSession } from "users/__session/session-context";
import SettingsSection from "users/settings/_views/settings-section";

/**
 * Who the person is to everyone else: their photo, name and contact details.
 *
 * These are kept on the marketplace profile, which is where colleagues and clients read them, so
 * Settings shows them and sends the person to that editor rather than keeping a second copy that
 * could disagree with it. The sign-in email is the account's own and cannot be changed yet.
 */
export default function AccountSettingsSection() {
  const session = useSession();
  const { individual, isError, isLoading, photo, retry } = useOwnIndividual("medium");
  const user = session.user;

  // The name colleagues see is the profile's, and "Edit photo and profile" changes that one. The
  // account keeps a name of its own that the profile editor does not touch, so it is only the
  // fallback while the profile is unavailable.
  const name = individual
    ? Name.create({ params: { firstName: individual.firstName, lastName: individual.lastName } })
    : user?.name;

  // "Not set" only once the profile has answered. While it loads, or when it could not be read, the
  // detail is unknown, and saying it is empty would be wrong.
  const notSet = isLoading || isError ? undefined : "Not set";
  const contactEmail = (individual?.email || undefined) ?? notSet;
  const phoneNumber = (individual?.phoneNumber || undefined) ?? notSet;

  return (
    <SettingsSection
      description="Your photo, name and contact details are on your marketplace profile. Colleagues and clients see them there and beside your work."
      title="Account"
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Avatar
          alt={photo ? "Your photo" : undefined}
          src={photo}
          sx={{ flexShrink: 0, fontSize: "1.5rem", height: 72, width: 72 }}
        >
          {name?.initials}
        </Avatar>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography component="p" sx={{ fontWeight: 600, overflowWrap: "anywhere" }} variant="h6">{name?.fullName}</Typography>
          {user?.companyName && (
            <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }} variant="body2">
              {user.companyName}
            </Typography>
          )}
          {!photo && !isLoading && !isError && (
            <Typography sx={{ color: "text.secondary", pt: 0.5 }} variant="body2">
              You have no photo. People see your initials.
            </Typography>
          )}
        </Stack>
      </Stack>
      {isError && (
        <Alert
          action={<Button color="inherit" onClick={retry} size="small">Try again</Button>}
          severity="warning"
        >
          Your profile could not be loaded, so your photo and contact details are not shown.
        </Alert>
      )}
      <Stack component="dl" spacing={2} sx={{ m: 0 }}>
        <SettingsDetail label="Sign-in email" value={user?.email?.toString()} />
        <SettingsDetail label="Contact email" value={contactEmail} />
        <SettingsDetail label="Phone" value={phoneNumber} />
      </Stack>
      <div>
        <Button
          component={RouterLink}
          startIcon={<EditIcon />}
          to={ownIndividualProfileRoute(user?.individualId?.value)}
          variant="outlined"
        >
          Edit photo and profile
        </Button>
      </div>
    </SettingsSection>
  );
}

function SettingsDetail(props: Readonly<{ label: string; value?: string }>) {
  return (
    <Stack spacing={0.25}>
      <Typography component="dt" sx={{ color: "text.secondary" }} variant="body2">{props.label}</Typography>
      <Typography component="dd" sx={{ m: 0, overflowWrap: "anywhere" }} variant="body1">{props.value ?? "—"}</Typography>
    </Stack>
  );
}
